/** @file loadGuideMasks: the server's silhouette of each slot a guide shows, decoded (missing ones skipped). */
import { decodePng } from '@zilo/pose3d-image-io/png/decodePng';
import type { Image } from '@zilo/pose3d-image-io/types/image';
import { webZlibCodec } from '@zilo/pose3d-image-io/zlib/webZlibCodec';
import type { ArtDeps } from '../artTypes';

/** One mask: `slotMask` (silhouette) for its signed URL, then the PNG fetched and decoded; null on any failure. */
async function maskOf(deps: Pick<ArtDeps, 'slotMask'>, puppetId: string, slot: string): Promise<Image | null> {
  try {
    const { url } = await deps.slotMask(puppetId, slot, 'silhouette');
    const bytes = new Uint8Array(await (await fetch(url)).arrayBuffer());
    return await decodePng(bytes, webZlibCodec);
  } catch {
    return null;
  }
}

/**
 * Slot key -> silhouette for `slots`, fetched in parallel through the same `slot.renderMask` path as the mask
 * download; a slot whose mask cannot be had is left out (its guide region is then the whole tile).
 */
export async function loadGuideMasks(deps: Pick<ArtDeps, 'slotMask'>, puppetId: string, slots: readonly string[]): Promise<Map<string, Image>> {
  const masks = await Promise.all(slots.map(async (slot) => [slot, await maskOf(deps, puppetId, slot)] as const));
  return new Map(masks.filter((m): m is readonly [string, Image] => m[1] !== null));
}
